//
// Explain mode's markers: while on, every explainable element on the page is
// marked at once, plus the side-menu entry of each page that has an
// introduction, and clicking a marker opens that explanation. Tab cycles through
// them, opening the next explanation when one is already open; Escape, or using
// anything else on the page, leaves the mode. The mode's own controls live in the
// Aide menu (HelpMenu), so nothing here can cover a page button. Rendered through
// a body portal so marker positions are the viewport positions driver.js also
// works from. Mounted once in the dashboard.
//

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';

import {
  useTownAssets,
  useTutorial,
} from '../stores';
import explainRegistry from '../tutorials/explainRegistry';

import ExplainMarker from './ExplainMarker';

const markerSize = 22;
// A page marker sits in the entry's left padding, ahead of its icon.
const menuMarkerInset = 4;
// Several records may explain one element (a chat panel has its menu and its
// visibility rules): their markers line up leftwards along the top edge.
const markerSpacing = 26;
// Wait for the screen to settle after the last DOM mutation before re-measuring.
const settleMs = 150;

const elementRect = (selector) => {
  const element = selector ? document.querySelector(selector) : null;
  if (!element) return null;
  const rect = element.getBoundingClientRect();
  return (rect.width || rect.height) ? { element, rect } : null;
};

// A menu record sits on the first of its side-menu entries currently shown.
const recordRect = (record) => {
  if (!record.menuSelector) return elementRect(record.selector);
  let found = null;
  (record.menuSelector ?? []).some((selector) => {
    found = elementRect(selector);
    return Boolean(found);
  });
  return found;
};

// What a click may land on without leaving the mode: its own markers and bar, an
// open explanation, and the Aide entries (the way out, and the tutorials list).
const staysInMode = (target) => Boolean(
  target.closest?.('[data-explain-layer], .driver-popover, .driver-overlay, #helpSection'),
);

function ExplainOverlay() {
  const { tutorial } = useTutorial();
  const {
    explainOn, setExplainMode, explainItem, isExplained, eligible,
    sessionExplained, busyKind, endActiveTutorial,
  } = tutorial;
  const { assets } = useTownAssets();
  const { translate } = assets;
  const location = useLocation();
  const [measureTick, setMeasureTick] = useState(0);
  // The marker Tab last landed on, so cycling resumes from there.
  const focusedIdRef = useRef(null);
  const markersRef = useRef([]);
  const busyKindRef = useRef(null);
  busyKindRef.current = busyKind;

  const remeasure = useCallback(() => setMeasureTick((tick) => tick + 1), []);

  const markers = useMemo(
    () => {
      if (!explainOn) return [];
      const perElement = new Map();
      const list = [];
      // Only the section the user is in: a marker on every other section's entry
      // would offer to describe pages that are not on screen.
      const section = location.pathname.split('/')[1] || 'home';
      Object.entries(explainRegistry).forEach(([id, record]) => {
        if ((record.scope === 'tour') || (!eligible(record))) return;
        const onMenu = Boolean(record.menuSelector);
        if (onMenu && (record.page.split('/')[0] !== section)) return;
        // An element record naming a page belongs to that route only.
        if ((!onMenu) && record.page && (!location.pathname.startsWith(`/${record.page}`))) return;
        const found = recordRect(record);
        if (!found) return;
        const shift = perElement.get(found.element) ?? 0;
        perElement.set(found.element, shift + 1);
        const left = onMenu
          ? (found.rect.left + menuMarkerInset)
          : Math.max(
              4,
              Math.min(
                found.rect.right - (markerSize / 2) - (shift * markerSpacing),
                window.innerWidth - markerSize - 4,
              ),
            );
        const top = onMenu
          ? (found.rect.top + ((found.rect.height - markerSize) / 2))
          : Math.max(4, found.rect.top - (markerSize / 2));
        list.push({
          id, record, left, top,
        });
      });
      // Screen order, so Tab reads top to bottom.
      list.sort((a, b) => (a.top - b.top) || (a.left - b.left));
      return list;
    },
    // measureTick and sessionExplained drive re-measure and marker state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [explainOn, eligible, measureTick, sessionExplained, location.pathname],
  );
  markersRef.current = markers;

  const openMarker = useCallback(
    (marker) => {
      focusedIdRef.current = marker.id;
      explainItem(marker.id);
    },
    [explainItem],
  );

  useEffect(
    () => {
      if (!explainOn) return undefined;
      let timer;
      const schedule = () => {
        clearTimeout(timer);
        timer = setTimeout(remeasure, settleMs);
      };
      const onKeyDown = (event) => {
        // A tour owns the keyboard while it plays.
        if (busyKindRef.current === 'tour') return;
        if (event.key === 'Escape') {
          // driver.js closes its own popover on Escape; the mode ends on the next one.
          if (busyKindRef.current === null) setExplainMode(false);
          return;
        }
        if (event.key !== 'Tab') return;
        const list = markersRef.current;
        if (!list.length) return;
        event.preventDefault();
        const current = list.findIndex((marker) => marker.id === focusedIdRef.current);
        const step = event.shiftKey ? -1 : 1;
        const next = list[(current + step + list.length) % list.length];
        focusedIdRef.current = next.id;
        document.querySelector(`[data-explain-marker="${next.id}"]`)?.focus();
        // With an explanation open, Tab moves straight on to the next one.
        if (busyKindRef.current === 'single') {
          endActiveTutorial();
          openMarker(next);
        }
      };
      // Using the page for real ends the mode, so a tour or a wizard never starts
      // under the markers.
      const onPointerDown = (event) => {
        if (!staysInMode(event.target)) setExplainMode(false);
      };
      const observer = new MutationObserver(schedule);
      observer.observe(document.body, { childList: true, subtree: true, attributes: true });
      window.addEventListener('scroll', schedule, true);
      window.addEventListener('resize', schedule);
      document.addEventListener('keydown', onKeyDown, true);
      document.addEventListener('pointerdown', onPointerDown, true);
      schedule();
      return () => {
        observer.disconnect();
        clearTimeout(timer);
        window.removeEventListener('scroll', schedule, true);
        window.removeEventListener('resize', schedule);
        document.removeEventListener('keydown', onKeyDown, true);
        document.removeEventListener('pointerdown', onPointerDown, true);
      };
    },
    [explainOn, remeasure, setExplainMode, endActiveTutorial, openMarker],
  );

  if (!explainOn) return null;

  return createPortal(
    <div data-explain-layer className="fixed inset-0 z-[9000] pointer-events-none">
      {markers.map((marker) => {
        const read = isExplained(marker.id);
        const state = translate(read ? 'explainMarkerRead' : 'explainMarkerUnread', 'tutorial');
        return (
          <ExplainMarker
            key={marker.id}
            id={marker.id}
            label={`${translate(marker.record.titleKey, 'tutorial')} — ${state}`}
            read={read}
            left={marker.left}
            top={marker.top}
            onClick={() => openMarker(marker)}
          />
        );
      })}
    </div>,
    document.body,
  );
}

export default ExplainOverlay;
